import React, { useState, useCallback, useMemo, forwardRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import {
  Link,
  OpenExpandedIcon,
  CancelCrossIcon,
  Body1,
  Caption1,
  LayerContextProvider,
  IconButton,
} from "@activecollab/components";
import PropTypes from "prop-types";
import Logo from "~/components/Logo.js";
import UserLink from "~/components/UserLink/UserLink";
import { PROJECT_TEMPLATE_FILE } from "~/pages/ProjectTemplates/utils.js";
import { restoreFileFromTrash } from "~/store/actions/actions.files.jsx";
import { permanentlyDelete } from "~/store/actions/actions.trash.jsx";
import { makeGetFileOrAttachmentByTypeAndId } from "~/store/selectors/selector.files.jsx";
import { isGoogleDriveFile as checkIsGoogleDriveFile, isDropboxFile as checkIsDropboxFile } from "~/utils/files.jsx";
import { keyCodes } from "~/utils/key_codes";
import { DownloadButton, DropboxButton, GoogleDriveButton } from "./Buttons/index.jsx";
import { useFilePreviewContext } from "./context/FilePreviewContext.jsx";
import { useFileTokenContext } from "./context/FileTokenContext.js";
import {
  DropboxPreview,
  FailedPreview,
  GoogleDrivePreview,
  ImagePreview,
  UnknownPreview,
  VideoPreview,
  VideoPreviewMessage,
  GenericPreview,
  NotFoundPreview,
  PdfDocumentPreview,
  PdfDocumentPreviewMessage,
  OtherDocumentPreview,
  AudioPreview,
  AudioPreviewMessage,
  LockedPreview,
} from "./Elements/index.jsx";
import { FilePreviewStatusChecker } from "./FilePreviewStatusChecker.jsx";
import { FilePreviewWrapper } from "./FilePreviewWrapper.jsx";
import { Navigation } from "./Navigation/index.jsx";
import * as statusCodes from "./statusCodes.jsx";
import { fileType } from "./types.jsx";
import { FormatLocalDateTime } from "../Formatters/index.jsx";
import { useFilePermission } from "../Hooks/security/useFilePermission.js";
import { TrashSlate } from "../TrashSlate/TrashSlate.js";

export const FilePreview = forwardRef(({ files, style, currentId, onClose }, ref) => {
  const { t } = useTranslation();
  const { preview_token } = useFileTokenContext();
  const { closeFilePreview } = useFilePreviewContext();
  const [status, setStatus] = useState(statusCodes.PREVIEW_UNKNOWN);
  const [progress, setProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const index = files.findIndex((file) => file?.id === currentId);
    return index < 0 ? 0 : index;
  });

  const previewFileId = files[currentIndex].id;

  const previewFileType = files[currentIndex].class ?? files[currentIndex].type;

  const getFileOrAttachmentByTypeAndId = useMemo(
    () => makeGetFileOrAttachmentByTypeAndId(previewFileType, previewFileId),
    [previewFileId, previewFileType]
  );
  const rawFile = useSelector(getFileOrAttachmentByTypeAndId);
  const file = useMemo(() => rawFile?.toJS(), [rawFile]);

  const isTrashed = useMemo(() => file?.is_trashed, [file?.is_trashed]);

  const filePermission = useFilePermission(file?.id);

  const dispatch = useDispatch();

  useUpdateEffect(() => {
    const index = files.findIndex((file) => file?.id === currentId);
    setCurrentIndex(index < 0 ? 0 : index);
  }, [currentId, files]);

  const fileMetadata = useMemo(() => {
    return {
      total: files.length,
      fileType: file?.class === PROJECT_TEMPLATE_FILE ? file?.type : file?.class,
    };
  }, [file, files.length]);

  const { total, fileType } = fileMetadata;

  const isGoogleDriveFile = useMemo(() => checkIsGoogleDriveFile(fileType), [fileType]);

  const isDropboxFile = useMemo(() => checkIsDropboxFile(fileType), [fileType]);

  const previewUrl = useMemo(() => {
    return file?.preview_url.replace("--PREVIEW-TOKEN--", preview_token);
  }, [file?.preview_url, preview_token]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prevValue) => {
      let nextValue = prevValue - 1;

      if (nextValue < 0) {
        nextValue = total - 1;
      }

      return nextValue;
    });
  }, [total]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevValue) => {
      let nextValue = prevValue + 1;

      if (nextValue > total - 1) {
        nextValue = 0;
      }

      return nextValue;
    });
  }, [total]);

  const handleKeyDown = useCallback(
    (event) => {
      if (total > 1) {
        if (event.key === keyCodes.left) {
          handlePrev();
        } else if (event.key === keyCodes.right) {
          handleNext();
        }
      }
    },
    [handlePrev, handleNext, total]
  );

  const MediaElement = useMemo(() => {
    if (isGoogleDriveFile) {
      return GoogleDrivePreview;
    } else if (isDropboxFile) {
      return DropboxPreview;
    }

    switch (status) {
      case statusCodes.PREVIEW_UNKNOWN: {
        return UnknownPreview;
      }
      case statusCodes.PREVIEW_FAILED: {
        return FailedPreview;
      }
      case statusCodes.PREVIEW_NOT_AVAILABLE: {
        return GenericPreview;
      }
      case statusCodes.PREVIEW_ORIGINAL_MISSING: {
        return NotFoundPreview;
      }
      case statusCodes.PREVIEW_LOCKED: {
        return LockedPreview;
      }
      default:
        break;
    }

    const file_format = file?.mime_type.slice(0, 5);

    switch (file_format) {
      case "video": {
        return status < statusCodes.PREVIEW_READY ? VideoPreviewMessage : VideoPreview;
      }
      case "audio": {
        return status < statusCodes.PREVIEW_READY ? AudioPreviewMessage : AudioPreview;
      }
      case "appli": {
        return status < statusCodes.PREVIEW_READY ? PdfDocumentPreviewMessage : PdfDocumentPreview;
      }
      case "image": {
        return ImagePreview;
      }
      default: {
        return OtherDocumentPreview;
      }
    }
  }, [file?.mime_type, isGoogleDriveFile, isDropboxFile, status]);

  const handleDelete = useCallback(async () => {
    closeFilePreview();
    await dispatch(permanentlyDelete("File", file?.id));
  }, [closeFilePreview, dispatch, file?.id]);

  const handleRestore = useCallback(async () => {
    await dispatch(restoreFileFromTrash(file?.id));
  }, [dispatch, file?.id]);

  const handleStatusCheck = useCallback(({ status, progress }) => {
    setStatus(status);
    setProgress(progress);
  }, []);

  if (!file) return null;

  return (
    <LayerContextProvider value={{ zIndex: 15000 }}>
      <FilePreviewStatusChecker
        url={previewUrl}
        check={!isGoogleDriveFile && !isDropboxFile && status === statusCodes.PREVIEW_UNKNOWN}
        onCheck={handleStatusCheck}
      >
        <FilePreviewWrapper
          ref={ref}
          style={style}
          className="ang_preview tw-bg-body-bg-main"
          onKeyDown={handleKeyDown}
        >
          <div className="ang_preview_meta tw-bg-page-paper-main">
            <div className="ang_preview_meta_container">
              <Logo
                className="icon icon_application_logo_white"
                fill="white"
              />

              <span className="ang_preview_name tw-text-theme-900">{file.name}</span>
              <br />
              {file?.class !== PROJECT_TEMPLATE_FILE ? (
                <span className="ang_preview_creator">
                  {t("Uploaded by")}{" "}
                  <UserLink
                    id={file.created_by_id}
                    name={file.created_by_name}
                    email={file.created_by_email}
                    hideAvatar
                    notInteractive
                    renderAs={Caption1}
                    nameProps={{ color: "secondary" }}
                    context="file preview"
                  />{" "}
                  {t("on")} <FormatLocalDateTime value={file.created_on} />
                </span>
              ) : (
                <span className="ang_preview_creator">
                  {t("Uploaded on")} <FormatLocalDateTime value={file.created_on} />
                </span>
              )}
              <span className="ang_preview_options tw-mr-4">
                {isDropboxFile && !isTrashed ? <DropboxButton url={previewUrl} /> : null}
                {isGoogleDriveFile && !isTrashed ? <GoogleDriveButton url={previewUrl} /> : null}
                {!isDropboxFile && !isGoogleDriveFile && !isTrashed ? (
                  <DownloadButton
                    fileSize={file.sizeValue || file.size}
                    url={file.download_url}
                  />
                ) : null}
              </span>

              {total > 1 ? (
                <Navigation
                  onPrev={handlePrev}
                  onNext={handleNext}
                  total={total}
                  current={currentIndex + 1}
                />
              ) : null}

              <span className="ang_preview_actions">
                {!isTrashed ? (
                  <Link
                    href={previewUrl}
                    className="ang_preview_full icon icon_full_white tw-mr-2"
                    title="Open Link in New Tab"
                    target="_blank"
                    rel="noreferrer"
                    variant="text gray"
                  >
                    <OpenExpandedIcon />
                  </Link>
                ) : null}
                <IconButton
                  className="ang_preview_close icon icon_delete_white"
                  onClick={onClose}
                  variant="text gray"
                >
                  <CancelCrossIcon />
                </IconButton>
              </span>
            </div>
          </div>
          <TrashSlate
            type="file"
            className="tw-h-full tw-items-center"
            subtitle={
              <Body1
                align="center"
                color="primary"
                className="tw-text-center tw-text-theme-900"
              >
                <Trans>This file is deleted</Trans>.
              </Body1>
            }
            isTrashed={isTrashed}
            trashedOn={file.trashed_on}
            trashedById={file.trashed_by_id}
            createdById={file.created_by_id}
            createdByName={file.created_by_name}
            createdOn={file.created_on}
            canRestore={filePermission.restore}
            canPermanentlyDelete={filePermission.update}
            onRestore={handleRestore}
            onPermanentlyDelete={handleDelete}
          >
            <MediaElement
              url={previewUrl}
              downloadUrl={file.download_url}
              sizeValue={file.sizeValue ?? file.size}
              progress={progress}
            />
          </TrashSlate>
        </FilePreviewWrapper>
      </FilePreviewStatusChecker>
    </LayerContextProvider>
  );
});

FilePreview.propTypes = {
  files: PropTypes.arrayOf(fileType),
  currentId: PropTypes.number,
  onClose: PropTypes.func,
  style: PropTypes.object,
};

FilePreview.defaultProps = {
  files: [],
  currentId: 0,
};

FilePreview.displayName = "FilePreview";
